import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { normAmbito } from '../utils/rutaTipo';

/**
 * Tipos NAVEGABLES del usuario actual (II.6 Capa 2).
 *
 * Clon de `useAssetTypes` pero contra `GET /tipos/navegables`, que el backend
 * filtra por inv_type_access (can_view). Lo usan el subsidebar y la landing
 * para pintar solo las "puertas" que el usuario puede abrir. Super Admin recibe
 * todos los tipos (bypass en el backend).
 *
 * NO sustituye a `useAssetTypes` (ese sigue sirviendo la lista COMPLETA para la
 * pantalla de gestión de Tipos). Se mantienen separados a propósito.
 *
 * Se refresca solo al recibir el evento global 'inventario:tipos-changed'
 * (mismo que emite la gestión de tipos al crear/editar/borrar).
 *
 * RESOLUCIÓN DE TIPOS — el slug NO es clave.
 *   `inv_asset_types` es UNIQUE (scope, slug): dos tipos homónimos en
 *   ámbitos distintos comparten slug. Buscar por slug a secas devuelve
 *   uno de los dos de forma indeterminada. Por eso el hook expone las
 *   DOS formas legítimas de resolver un tipo, y son el ÚNICO sitio
 *   donde se hace:
 *
 *     buscarTipo(scope, slug) → el tipo de ese ámbito, o null
 *     tipoPorId               → Map de id (string) → tipo
 *
 *   `tipoPorId` es lo que necesitan los selectores: el <option> lleva
 *   el id (único de verdad) y quien consulta traduce a (slug, scope)
 *   aquí, no por su cuenta.
 */
/**
 * Índice id (como string, que es lo que devuelve un <select>) → tipo.
 *
 * ÚNICA implementación de la traducción id → tipo (y de ahí a su par
 * (slug, scope)). La usa este hook para su `tipoPorId`, y la importa
 * cualquier pantalla que tenga su propio catálogo —el form de reglas de
 * aviso carga la lista COMPLETA vía /tipos, no la navegable— para no
 * reimplementar la traducción por su cuenta.
 */
export function indexarPorId(tipos) {
  return new Map((tipos || []).map((t) => [String(t.id), t]));
}

export function useTiposNavegables() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTipos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/tipos/navegables');
      // Robusto a la forma de la respuesta del wrapper `api`:
      //  - { data: [...] }  (cuerpo { data, count })
      //  - [...]            (array directo)
      const payload = response && response.data !== undefined ? response.data : response;
      const lista = Array.isArray(payload)
        ? payload
        : (payload && Array.isArray(payload.data) ? payload.data : []);
      setTipos(lista);
      setError(null);
    } catch (e) {
      setError(e);
      setTipos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTipos();
  }, [fetchTipos]);

  useEffect(() => {
    const handler = () => fetchTipos();
    window.addEventListener('inventario:tipos-changed', handler);
    return () => window.removeEventListener('inventario:tipos-changed', handler);
  }, [fetchTipos]);

  const tipoPorId = useMemo(() => indexarPorId(tipos), [tipos]);

  // Resolución por la clave natural. `scope` ausente se normaliza a
  // 'interno', igual que en el backend, para que una ruta antigua sin
  // ámbito no encuentre por casualidad el tipo colaborador.
  const buscarTipo = useCallback(
    (scope, slug) => {
      const eff = normAmbito(scope);
      return (
        tipos.find((t) => t.slug === slug && normAmbito(t.scope) === eff) || null
      );
    },
    [tipos]
  );

  // Ámbitos en los que existe un slug. Lo usa la ruta de compatibilidad
  // para decidir a dónde mandar un enlace antiguo sin ámbito.
  const ambitosDe = useCallback(
    (slug) => tipos.filter((t) => t.slug === slug).map((t) => normAmbito(t.scope)),
    [tipos]
  );

  return {
    tipos,
    loading,
    error,
    refresh: fetchTipos,
    tipoPorId,
    buscarTipo,
    ambitosDe,
  };
}
